import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { partnerApi } from '@/lib/api';
import { ArrowLeft, LogIn, Users, Globe, Shield, Pencil, Check, X, UserPlus, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import styles from './TenantDetailPage.module.css';

const ROLES = ['VIEWER', 'DEVELOPER', 'OPERATOR', 'ADMIN'];
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL ?? 'http://localhost:3002';

interface EditForm {
  name: string;
  status: string;
  plan: string;
  allowedDomain: string;
  autoProvision: boolean;
}

export default function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_auth/tenants/$tenantId' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editing, setEditing]           = useState(false);
  const [form, setForm]                 = useState<EditForm | null>(null);
  const [inviteOpen, setInviteOpen]     = useState(false);
  const [inviteForm, setInviteForm]     = useState({ email: '', name: '', role: 'ADMIN' });
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [seedResult, setSeedResult] = useState<Record<string, number> | null>(null);
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);

  const tenantQ = useQuery({
    queryKey: ['partner', 'tenant', tenantId],
    queryFn: () => partnerApi.tenants.get(tenantId),
  });

  const [impersonating, setImpersonating] = useState(false);

  async function handleImpersonate() {
    // Must open window synchronously inside user gesture — browser blocks popup if async
    const newTab = window.open('about:blank', '_blank');
    setImpersonating(true);
    try {
      const data = await partnerApi.tenants.impersonate(tenantId);
      const url = `${PORTAL_URL}/auth/impersonate?token=${encodeURIComponent(data.token)}`;
      if (newTab) {
        newTab.location.href = url;
      } else {
        // Fallback if still blocked — navigate current tab
        window.location.href = url;
      }
    } catch {
      newTab?.close();
    } finally {
      setImpersonating(false);
    }
  }

  const updateMutation = useMutation({
    mutationFn: (data: Partial<EditForm>) => partnerApi.tenants.update(tenantId, {
      ...data,
      allowedDomain: data.allowedDomain || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['partner', 'tenants'] });
      setEditing(false);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => partnerApi.tenants.inviteUser(tenantId, inviteForm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'tenant', tenantId] });
      setInviteOpen(false);
      setInviteForm({ email: '', name: '', role: 'ADMIN' });
    },
  });

  const roleChangeMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      partnerApi.tenants.updateUserRole(tenantId, userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner', 'tenant', tenantId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => partnerApi.tenants.removeUser(tenantId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'tenant', tenantId] });
      setConfirmRemove(null);
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => partnerApi.tenants.seed(tenantId),
    onSuccess: (data) => {
      setSeedResult(data.created);
      qc.invalidateQueries({ queryKey: ['partner', 'tenant', tenantId] });
    },
  });

  const tenant = tenantQ.data;
  if (tenantQ.isLoading) return <div className={styles.loading}>Loading tenant…</div>;
  if (!tenant) return <div className={styles.loading}>Tenant not found.</div>;

  const regularUsers = tenant.users.filter((u) => !u.isSupportAccount);
  const supportAccts = tenant.users.filter((u) => u.isSupportAccount);

  function startEdit() {
    setForm({ name: tenant!.name, status: tenant!.status, plan: tenant!.plan, allowedDomain: tenant!.allowedDomain ?? '', autoProvision: tenant!.autoProvision });
    setEditing(true);
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.back} onClick={() => navigate({ to: '/tenants' })}>
          <ArrowLeft size={14} /> Tenants
        </button>
        <div className={styles.titleRow}>
          <div className={styles.avatar}>{tenant.name[0]?.toUpperCase()}</div>
          <div>
            <h1 className={styles.title}>{tenant.name}</h1>
            <p className={styles.slug}>/{tenant.slug}</p>
          </div>
          <div className={styles.actions}>
            {!editing ? (
              <>
                <button className={styles.editBtn} onClick={startEdit}><Pencil size={13} /> Edit</button>
                <button
                  className={styles.seedBtn}
                  onClick={() => setSeedConfirmOpen(true)}
                  disabled={seedMutation.isPending || !!seedResult}
                >
                  {seedMutation.isPending ? <><Loader2 size={14} className={styles.spin} /> Seeding…</> : <><Sparkles size={14} /> Seed Demo Data</>}
                </button>
                <button className={styles.impersonateBtn} onClick={handleImpersonate} disabled={impersonating}>
                  <LogIn size={14} /> {impersonating ? 'Opening…' : 'Login as Tenant Admin'}
                </button>
              </>
            ) : (
              <>
                <button className={styles.cancelBtn} onClick={() => { setEditing(false); setForm(null); }} disabled={updateMutation.isPending}>
                  <X size={13} /> Cancel
                </button>
                <button className={styles.saveBtn} onClick={() => updateMutation.mutate(form!)} disabled={updateMutation.isPending || !form}>
                  <Check size={13} /> {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit form */}
      {editing && form && (
        <div className={styles.editForm}>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Name</label>
              <input className={styles.formInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Status</label>
              <select className={styles.formSelect} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="TRIAL">Trial</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Plan</label>
              <select className={styles.formSelect} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                <option value="TRIAL">Trial</option>
                <option value="STARTER">Starter</option>
                <option value="PROFESSIONAL">Professional</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Allowed Domain</label>
              <input className={styles.formInput} placeholder="e.g. acme.com" value={form.allowedDomain} onChange={(e) => setForm({ ...form, allowedDomain: e.target.value })} />
            </div>
            <div className={`${styles.formField} ${styles.formFieldFull}`}>
              <label className={styles.formLabel}>Auto-provision users</label>
              <label className={styles.toggle}>
                <input type="checkbox" checked={form.autoProvision} onChange={(e) => setForm({ ...form, autoProvision: e.target.checked })} />
                <span className={styles.toggleSlider} />
                <span className={styles.toggleLabel}>
                  {form.autoProvision ? 'On — new domain users sign in automatically as VIEWER' : 'Off — only invited users can sign in'}
                </span>
              </label>
            </div>
          </div>
          {updateMutation.isError && (
            <div className={styles.formError}>Failed to save: {(updateMutation.error as Error).message}</div>
          )}
        </div>
      )}


      {/* Info cards */}
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.cardLabel}><Globe size={13} /> Domain</div>
          <div className={styles.cardValue}>{tenant.allowedDomain ?? <span className={styles.cardEmpty}>None</span>}</div>
          <div className={styles.cardSub}>Auto-provision: <strong className={tenant.autoProvision ? styles.on : styles.off}>{tenant.autoProvision ? 'On' : 'Off'}</strong></div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}><Shield size={13} /> Plan</div>
          <div className={styles.cardValue}>{tenant.plan}</div>
          <div className={styles.cardSub}><span className={`${styles.badge} ${styles[`badge_${tenant.status.toLowerCase()}`]}`}>{tenant.status}</span></div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}><Users size={13} /> Users</div>
          <div className={styles.cardValue}>{regularUsers.length}</div>
          <div className={styles.cardSub}>{supportAccts.length} support account{supportAccts.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Seed result */}
      {seedResult && (
        <div className={styles.seedBanner}>
          <div className={styles.seedBannerHeader}>
            <Sparkles size={16} />
            <strong>Demo data seeded successfully!</strong>
            <button className={styles.seedDismiss} onClick={() => setSeedResult(null)}><X size={14} /></button>
          </div>
          <div className={styles.seedStats}>
            {Object.entries(seedResult).map(([key, count]) => (
              <span key={key} className={styles.seedStat}>{count} {key.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
            ))}
          </div>
        </div>
      )}

      {seedMutation.isError && (
        <div className={styles.formError}>
          Seed failed: {(seedMutation.error as Error).message}
        </div>
      )}

      {/* Users section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Users</h2>
          <button className={styles.addUserBtn} onClick={() => setInviteOpen(!inviteOpen)}>
            <UserPlus size={14} /> Invite user
          </button>
        </div>

        {/* Invite form */}
        {inviteOpen && (
          <div className={styles.inviteForm}>
            <div className={styles.inviteGrid}>
              <input
                className={styles.formInput}
                placeholder="email@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
              <input
                className={styles.formInput}
                placeholder="Full name (optional)"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
              <select
                className={styles.formSelect}
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button
                className={styles.inviteSubmitBtn}
                onClick={() => inviteMutation.mutate()}
                disabled={!inviteForm.email || inviteMutation.isPending}
              >
                {inviteMutation.isPending ? 'Inviting…' : 'Invite'}
              </button>
            </div>
            {inviteMutation.isError && (
              <div className={styles.formError}>{(inviteMutation.error as Error).message}</div>
            )}
            <p className={styles.inviteHint}>
              The user will be pre-provisioned. They log in with Google and their account links automatically.
            </p>
          </div>
        )}

        {regularUsers.length === 0 ? (
          <div className={styles.emptyUsers}>No users yet — invite someone above, or they'll appear after signing in with a matching domain.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {regularUsers.map((u) => (
                <tr key={u.id}>
                  <td className={styles.emailCell}>
                    {u.email}
                    {!u.lastLoginAt && <span className={styles.pendingTag}>invited</span>}
                  </td>
                  <td>{u.name ?? '—'}</td>
                  <td>
                    <select
                      className={styles.roleSelect}
                      value={u.role}
                      onChange={(e) => roleChangeMutation.mutate({ userId: u.id, role: e.target.value })}
                      disabled={roleChangeMutation.isPending}
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                  <td>
                    {confirmRemove === u.id ? (
                      <div className={styles.confirmRow}>
                        <button className={styles.confirmYes} onClick={() => removeMutation.mutate(u.id)} disabled={removeMutation.isPending}>Yes, remove</button>
                        <button className={styles.confirmNo} onClick={() => setConfirmRemove(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button className={styles.removeBtn} onClick={() => setConfirmRemove(u.id)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Seed confirmation dialog */}
      <ConfirmDialog
        open={seedConfirmOpen}
        onCancel={() => setSeedConfirmOpen(false)}
        onConfirm={() => { setSeedConfirmOpen(false); seedMutation.mutate(); }}
        title="Seed Demo Data"
        description="This will populate the tenant with demo agents, skills, tools, workflows, and knowledge articles. This only works on fresh tenants with no existing agents."
        confirmLabel="Seed Tenant"
        variant="info"
      />
    </div>
  );
}
